/**
 * Rotates the announcement bar's USP items on narrow viewports, one at a time.
 *
 * Round 19 item 7. Below 990 the bar used to wrap onto a second row and drop all but one USP
 * (`.announcebar__usp:nth-child(n+2)` below 750), which cost 23px of chrome on every phone page and
 * still showed only one of the four. Rotating in place keeps the bar to a single row AND surfaces
 * every USP over time, instead of permanently hiding three of them.
 *
 * It is progressive enhancement, not a dependency: with JS off the CSS shows the first item and the
 * bar still renders as a single correct row. Nothing here creates layout — the stacking is CSS.
 *
 * Accessibility:
 *   - every item stays in the DOM and none is aria-hidden, so a screen reader reads all four in
 *     order and loses nothing to the rotation.
 *   - the list is not a live region. Announcing a decorative marketing line every few seconds
 *     would interrupt whatever the user is actually reading.
 *   - prefers-reduced-motion stops the rotation entirely and pins item 1.
 */
(() => {
  const INTERVAL_MS = 4000
  // MUST equal the rotating-layout band in assets/section-announcebar.css (max-width: 1039px).
  // They were 989 and 1039 for one release: between those two widths the CSS stacked all four USPs
  // into a single grid cell while the JS thought it was on desktop and never advanced, so items
  // 2-4 sat at opacity:0 permanently. Change one, change the other.
  const NARROW = '(max-width: 1039px)'
  // Below this the CSS hides the USP block outright, so there is nothing to advance.
  const USPS_HIDDEN = '(max-width: 599px)'
  const REDUCED = '(prefers-reduced-motion: reduce)'

  class AnnouncebarRotator {
    constructor(list) {
      this.list = list
      this.items = Array.from(list.querySelectorAll('.announcebar__usp'))
      this.index = 0
      this.timer = null
      this.narrow = window.matchMedia(NARROW)
      this.hidden = window.matchMedia(USPS_HIDDEN)
      this.reduced = window.matchMedia(REDUCED)

      if (this.items.length < 2) return

      this.sync = this.sync.bind(this)
      this.onVisibility = this.onVisibility.bind(this)

      this.narrow.addEventListener('change', this.sync)
      this.hidden.addEventListener('change', this.sync)
      this.reduced.addEventListener('change', this.sync)
      document.addEventListener('visibilitychange', this.onVisibility)
      this.sync()
    }

    /** Decide from the two media queries whether the rotation should be running at all. */
    sync() {
      // Not below 600: the CSS hides the whole block there, and a timer repainting a display:none
      // element every 4s is pure waste on exactly the devices least able to afford it.
      const shouldRun = this.narrow.matches && !this.hidden.matches && !this.reduced.matches
      this.stop()
      // Always reset to the first item: leaving item 3 active while the desktop rules take over
      // would hide items 1, 2 and 4 behind an inline opacity that CSS no longer controls.
      this.show(0)
      if (shouldRun) this.start()
    }

    show(i) {
      this.index = i % this.items.length
      this.items.forEach((el, n) => el.classList.toggle('is-active', n === this.index))
    }

    start() {
      this.timer = window.setInterval(() => this.show(this.index + 1), INTERVAL_MS)
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer)
      this.timer = null
    }

    /** A background tab should not burn a timer repainting something nobody can see. */
    onVisibility() {
      if (document.hidden) this.stop()
      else this.sync()
    }
  }

  const init = () => {
    document.querySelectorAll('.announcebar__usps').forEach((list) => {
      if (list.dataset.rotatorReady) return
      list.dataset.rotatorReady = 'true'
      new AnnouncebarRotator(list)
    })
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
  else init()

  // Shopify re-renders a section's markup when the merchant edits it in the theme editor, which
  // replaces the <ul> this was bound to. Without this the rotation looks broken to the one person
  // who is actively configuring it.
  document.addEventListener('shopify:section:load', init)
})()
